import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { TranslationService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Form, Input, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const TranslationFormPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();

  const { data: translation } = useQuery({
    queryKey: ["translation", id],
    queryFn: () => TranslationService.getTranslationById(id ?? ""),
    enabled: !!id,
  });

  const handleSubmitForm = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    if (isEditMode) {
      // Check for changes before updating
      if (!checkFormChanges(values, translation!, notify)) {
        setIsSubmitting(false);
        return;
      }

      const response = await TranslationService.updateTranslation(
        id ?? "",
        values
      );
      if (response) {
        navigate(navigationRoutes.TRANSLATIONS);
      }
    } else {
      const response = await TranslationService.createTranslation(values);
      if (response) {
        navigate(navigationRoutes.TRANSLATIONS);
      }
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (translation) {
      form.setFieldsValue(translation);
      setIsEditMode(true);
    }
  }, [translation, form]);

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Translation" : "Create New Translation"}
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmitForm}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode ? "Save Translation Changes" : "Save New Translation"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmitForm}
        className="max-w-2xl"
      >
        <Form.Item
          name="language"
          label="Language"
          rules={[{ required: true, message: "Please select a language" }]}
          required
        >
          <Select>
            <Select.Option value="HI">Hindi</Select.Option>
            <Select.Option value="EN">English</Select.Option>
            {/* Add more languages as needed */}
          </Select>
        </Form.Item>

        <Form.Item
          name="source_text"
          label="Source Text"
          rules={[{ required: true, message: "Please enter source text" }]}
          required
        >
          <Input.TextArea style={{ resize: "none" }} rows={4} />
        </Form.Item>

        <Form.Item
          name="translated_text"
          label="Translated Text"
          rules={[{ required: true, message: "Please enter translated text" }]}
          required
        >
          <Input.TextArea style={{ resize: "none" }} rows={4} />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default TranslationFormPage;
